import { useEffect, useMemo, useRef } from "react";

const TITLE_WORDS = ["Flexible", "intelligence"];

type Letter = {
  char: string;
  index: number;
};

type Word = {
  letters: Letter[];
  text: string;
};

type LetterLayout = {
  pageX: number;
  pageY: number;
};

type LetterMotion = {
  aberration: number;
  aberrationAlpha: number;
  edgeGlow: number;
  edgeGlowAlpha: number;
  edgeGlowSoftAlpha: number;
  rotate: number;
  scaleX: number;
  scaleY: number;
  skewX: number;
  skewY: number;
  translateX: number;
  translateY: number;
};

type MotionState = {
  active: boolean;
  dragging: boolean;
  insideHero: boolean;
  lastFrameAt: number;
  intensity: number;
  lastScrollAt: number;
  lastScrollY: number;
  proximity: number;
  scrollCarry: number;
  scrollDirection: number;
  scrollEnergy: number;
  scrollReleaseAt: number;
  scrollVelocity: number;
  x: number;
  y: number;
};

type MotionSettings = {
  chroma: number;
  contactGlow: number;
  directness: number;
  edgeGlow: number;
  flex: number;
  glowSpread: number;
  lift: number;
  peak: number;
  push: number;
  radius: number;
  relax: number;
  scrollDamping: number;
  scrollResponse: number;
  scrollSpring: number;
  scrollTravel: number;
  scrollWarp: number;
};

const DEFAULT_SETTINGS: MotionSettings = {
  chroma: 0.62,
  contactGlow: 1.1,
  directness: 0.18,
  edgeGlow: 0.72,
  flex: 1.9,
  glowSpread: 0.9,
  lift: 2,
  peak: 0,
  push: 27,
  radius: 160,
  relax: 1,
  scrollDamping: 0.83,
  scrollResponse: 0.18,
  scrollSpring: 0.095,
  scrollTravel: 0.16,
  scrollWarp: 1.5,
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const createLetterMotion = (): LetterMotion => ({
  aberration: 0,
  aberrationAlpha: 0,
  edgeGlow: 0,
  edgeGlowAlpha: 0,
  edgeGlowSoftAlpha: 0,
  rotate: 0,
  scaleX: 1,
  scaleY: 1,
  skewX: 0,
  skewY: 0,
  translateX: 0,
  translateY: 0,
});

export function FlexibleTitle() {
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const letterRefs = useRef<HTMLSpanElement[]>([]);
  const frameRef = useRef<number | null>(null);
  const letterLayoutRef = useRef<LetterLayout[]>([]);
  const letterMotionRef = useRef<LetterMotion[]>([]);
  const settingsRef = useRef<MotionSettings>(DEFAULT_SETTINGS);
  const motionRef = useRef<MotionState>({
    active: false,
    dragging: false,
    insideHero: false,
    lastFrameAt: 0,
    intensity: 0,
    lastScrollAt: 0,
    lastScrollY: 0,
    proximity: 0,
    scrollCarry: 0,
    scrollDirection: 1,
    scrollEnergy: 0,
    scrollReleaseAt: 0,
    scrollVelocity: 0,
    x: 0,
    y: 0,
  });

  const words = useMemo<Word[]>(() => {
    let index = 0;

    return TITLE_WORDS.map((text) => ({
      text,
      letters: Array.from(text).map((char) => ({
        char,
        index: index++,
      })),
    }));
  }, []);

  useEffect(() => {
    const title = titleRef.current;
    const hero = title?.closest<HTMLElement>(".hero");

    if (!title || !hero || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const letters = letterRefs.current.filter(Boolean);
    const motion = motionRef.current;
    let disposed = false;

    motion.lastScrollAt = performance.now();
    motion.lastScrollY = window.scrollY;

    const resetLetters = () => {
      for (const letter of letters) {
        letter.style.transform = "";
        letter.style.removeProperty("--hero-aberration");
        letter.style.removeProperty("--hero-aberration-alpha");
        letter.style.removeProperty("--hero-edge-glow");
        letter.style.removeProperty("--hero-edge-glow-alpha");
        letter.style.removeProperty("--hero-edge-glow-soft-alpha");
      }

      letterMotionRef.current = letters.map(() => createLetterMotion());
    };

    const measureLetters = () => {
      const savedTransforms = letters.map((letter) => letter.style.transform);

      for (const letter of letters) {
        letter.style.transform = "";
      }

      letterLayoutRef.current = letters.map((letter) => {
        const rect = letter.getBoundingClientRect();

        return {
          pageX: rect.left + rect.width / 2 + window.scrollX,
          pageY: rect.top + rect.height / 2 + window.scrollY,
        };
      });

      letters.forEach((letter, index) => {
        letter.style.transform = savedTransforms[index] ?? "";
      });
    };

    const getPointerField = (clientX: number, clientY: number) => {
      const titleRect = title.getBoundingClientRect();
      const heroRect = hero.getBoundingClientRect();
      const visibleHeroBottom = Math.min(heroRect.bottom, window.innerHeight);
      const insideVisibleHero =
        clientX >= heroRect.left &&
        clientX <= heroRect.right &&
        clientY >= Math.max(heroRect.top, 0) &&
        clientY <= visibleHeroBottom;
      const marginX = clamp(settingsRef.current.radius * 0.9, 120, 520);
      const marginY = clamp(settingsRef.current.radius * 0.72, 90, 380);
      const outsideX = Math.max(titleRect.left - clientX, 0, clientX - titleRect.right);
      const outsideY = Math.max(titleRect.top - clientY, 0, clientY - titleRect.bottom);
      const normalizedDistance = Math.hypot(outsideX / marginX, outsideY / marginY);
      const rawTitleProximity = clamp(1 - normalizedDistance, 0, 1);
      const titleProximity = rawTitleProximity * rawTitleProximity;

      return {
        insideVisibleHero,
        proximity: titleProximity,
      };
    };

    const tick = (time: number) => {
      const currentSettings = settingsRef.current;
      const frameDelta = motion.lastFrameAt ? clamp((time - motion.lastFrameAt) / 16.67, 0.45, 2.2) : 1;
      const hasActiveInput = motion.dragging || motion.insideHero || motion.proximity > 0;
      const isScrollActive = time < motion.scrollReleaseAt;
      const heroFloor = motion.insideHero ? 0.055 : 0;
      const targetIntensity = hasActiveInput
        ? motion.dragging
          ? 1
          : Math.max(motion.proximity * 0.86, heroFloor)
        : 0;
      const response = hasActiveInput ? currentSettings.directness : 0.28;

      motion.lastFrameAt = time;

      motion.intensity += (targetIntensity - motion.intensity) * response;
      motion.scrollVelocity += -motion.scrollCarry * currentSettings.scrollSpring * frameDelta;
      motion.scrollVelocity *= Math.pow(currentSettings.scrollDamping, frameDelta);
      motion.scrollCarry = clamp(motion.scrollCarry + motion.scrollVelocity * frameDelta, -42, 42);
      motion.scrollEnergy *= Math.pow(isScrollActive ? 0.94 : 0.88, frameDelta);

      const shouldAnimate =
        hasActiveInput ||
        motion.intensity > 0.003 ||
        motion.scrollEnergy > 0.004 ||
        Math.abs(motion.scrollCarry) > 0.02;

      if (!shouldAnimate) {
        motion.lastFrameAt = 0;
        motion.scrollCarry = 0;
        motion.scrollVelocity = 0;
        resetLetters();
        frameRef.current = null;
        return;
      }

      const radius = currentSettings.radius;
      const layouts = letterLayoutRef.current;
      const titleRect = title.getBoundingClientRect();
      const titleCenterX = titleRect.left + titleRect.width / 2;
      const titleCenterY = titleRect.top + titleRect.height / 2;
      const scrollAmount = Math.abs(motion.scrollCarry);
      const scrollMaterial = clamp(scrollAmount / 26 + motion.scrollEnergy * 0.28, 0, 1);
      const letterResponse = clamp(
        currentSettings.scrollResponse + scrollMaterial * 0.18 + (1 - currentSettings.relax) * 0.7,
        0.12,
        0.82,
      );

      letters.forEach((letter, index) => {
        const layout = layouts[index];
        let centerX: number;
        let centerY: number;

        if (layout) {
          centerX = layout.pageX - window.scrollX;
          centerY = layout.pageY - window.scrollY;
        } else {
          const fallbackRect = letter.getBoundingClientRect();

          centerX = fallbackRect.left + fallbackRect.width / 2;
          centerY = fallbackRect.top + fallbackRect.height / 2;
        }

        const dx = centerX - motion.x;
        const dy = centerY - motion.y;
        const distance = Math.max(1, Math.hypot(dx, dy));
        const peakStrength = clamp(currentSettings.peak, 0, 1);
        const softCore = (1 - peakStrength) * 54;
        const vectorDistance = Math.max(1, Math.hypot(dx, dy, softCore));
        const falloff = Math.exp(-(distance * distance) / (2 * radius * radius));
        const rawInfluence = falloff * motion.intensity;
        const nearField = clamp(1 - distance / (radius * 0.2), 0, 1);
        const influence = rawInfluence * (1 - nearField * nearField * (1 - peakStrength) * 0.48);
        const normalizedIndex = letters.length > 1 ? index / (letters.length - 1) - 0.5 : 0;
        const normalizedX = titleRect.width > 0 ? clamp((centerX - titleCenterX) / (titleRect.width / 2), -1, 1) : 0;
        const normalizedY = titleRect.height > 0 ? clamp((centerY - titleCenterY) / (titleRect.height / 2), -1, 1) : 0;
        const materialDelay = 1 + normalizedY * 0.08 - normalizedIndex * 0.045 * motion.scrollDirection;
        const scrollLag = motion.scrollCarry * materialDelay * currentSettings.scrollTravel;
        const scrollShear = clamp(
          (-motion.scrollCarry * 0.035 - motion.scrollVelocity * 0.095) * currentSettings.scrollWarp,
          -8.5,
          8.5,
        );
        const scrollLean = clamp(
          (motion.scrollCarry * 0.011 + motion.scrollVelocity * 0.026) * normalizedX * currentSettings.scrollWarp,
          -2.6,
          2.6,
        );
        const scrollStretch = clamp(
          (scrollAmount * 0.022 + Math.abs(motion.scrollVelocity) * 0.026) * currentSettings.scrollWarp,
          0,
          1.75,
        );
        const directionX = dx / vectorDistance;
        const directionY = dy / vectorDistance;
        const pullUp = -currentSettings.lift * influence;
        const pushDistance = currentSettings.push * influence;
        const stretchContact = clamp(scrollStretch / 1.75 + influence * 0.32 + scrollMaterial * 0.34, 0, 1);
        const targetAberration = clamp(
          (Math.max(0, influence - 0.22) * (motion.dragging ? 2.4 : 1.4) + scrollMaterial * 0.12) *
            currentSettings.chroma,
          0,
          1.35,
        );
        const targetGlow = clamp(
          (scrollStretch * 0.52 + scrollMaterial * 0.36 + Math.max(0, influence - 0.08) * 0.42) *
            currentSettings.edgeGlow,
          0,
          1.8,
        );
        const targetGlowAlpha = clamp(targetGlow * (0.14 + currentSettings.contactGlow * 0.13), 0, 0.58);
        const target: LetterMotion = {
          aberration: targetAberration,
          aberrationAlpha: clamp(targetAberration * 0.11, 0, 0.2),
          edgeGlow: targetGlow,
          edgeGlowAlpha: targetGlowAlpha,
          edgeGlowSoftAlpha: clamp(targetGlowAlpha * (0.36 + stretchContact * currentSettings.contactGlow * 0.24), 0, 0.42),
          rotate: scrollLean,
          scaleX: 1 + influence * currentSettings.flex * 0.12 - scrollStretch * 0.012,
          scaleY: 1 - influence * currentSettings.flex * 0.045 + scrollStretch * 0.028,
          skewX: clamp(
            -directionX * influence * currentSettings.flex * 16 +
              scrollShear * (0.82 + Math.abs(normalizedX) * 0.18),
            -14,
            14,
          ),
          skewY: clamp(directionY * influence * currentSettings.flex * 7, -7, 7),
          translateX: clamp(directionX * pushDistance - scrollShear * 0.22 * normalizedY, -44, 44),
          translateY: clamp(directionY * pushDistance * 0.72 + pullUp + scrollLag, -42, 36),
        };
        const current = letterMotionRef.current[index] ?? createLetterMotion();

        current.translateX += (target.translateX - current.translateX) * letterResponse;
        current.translateY += (target.translateY - current.translateY) * letterResponse;
        current.skewX += (target.skewX - current.skewX) * letterResponse;
        current.skewY += (target.skewY - current.skewY) * letterResponse;
        current.scaleX += (target.scaleX - current.scaleX) * letterResponse;
        current.scaleY += (target.scaleY - current.scaleY) * letterResponse;
        current.rotate += (target.rotate - current.rotate) * letterResponse;
        current.aberration += (target.aberration - current.aberration) * letterResponse;
        current.aberrationAlpha += (target.aberrationAlpha - current.aberrationAlpha) * letterResponse;
        current.edgeGlow += (target.edgeGlow - current.edgeGlow) * letterResponse;
        current.edgeGlowAlpha += (target.edgeGlowAlpha - current.edgeGlowAlpha) * letterResponse;
        current.edgeGlowSoftAlpha += (target.edgeGlowSoftAlpha - current.edgeGlowSoftAlpha) * letterResponse;
        letterMotionRef.current[index] = current;

        letter.style.transform = `translate3d(${current.translateX.toFixed(2)}px, ${current.translateY.toFixed(
          2,
        )}px, 0) skew(${current.skewX.toFixed(2)}deg, ${current.skewY.toFixed(2)}deg) scale(${current.scaleX.toFixed(
          3,
        )}, ${current.scaleY.toFixed(3)}) rotate(${current.rotate.toFixed(2)}deg)`;
        letter.style.setProperty("--hero-aberration", `${current.aberration.toFixed(2)}px`);
        letter.style.setProperty("--hero-aberration-alpha", current.aberrationAlpha.toFixed(3));
        letter.style.setProperty(
          "--hero-edge-glow",
          `${(current.edgeGlow * (5 + currentSettings.glowSpread * 14)).toFixed(2)}px`,
        );
        letter.style.setProperty("--hero-edge-glow-alpha", current.edgeGlowAlpha.toFixed(3));
        letter.style.setProperty("--hero-edge-glow-soft-alpha", current.edgeGlowSoftAlpha.toFixed(3));
      });

      frameRef.current = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (frameRef.current == null) {
        frameRef.current = window.requestAnimationFrame(tick);
      }
    };

    const setPoint = (clientX: number, clientY: number) => {
      const field = getPointerField(clientX, clientY);

      motion.x = clientX;
      motion.y = clientY;
      motion.proximity = field.proximity;
      motion.insideHero = field.insideVisibleHero;
      motion.active = field.insideVisibleHero || field.proximity > 0 || motion.dragging;
      start();
    };

    const clearTouchInput = () => {
      motion.active = false;
      motion.dragging = false;
      motion.insideHero = false;
      motion.proximity = 0;
      start();
    };

    const handlePointerMove = (event: PointerEvent) => {
      setPoint(event.clientX, event.clientY);
    };

    const handlePointerDown = (event: PointerEvent) => {
      const field = getPointerField(event.clientX, event.clientY);

      motion.dragging = field.proximity > 0 || field.insideVisibleHero;
      setPoint(event.clientX, event.clientY);
    };

    const handlePointerUp = (event: PointerEvent) => {
      motion.dragging = false;

      if (event.pointerType !== "mouse") {
        clearTouchInput();
        return;
      }

      const field = getPointerField(event.clientX, event.clientY);

      motion.proximity = field.proximity;
      motion.insideHero = field.insideVisibleHero;
      motion.active = motion.insideHero || motion.proximity > 0;

      start();
    };

    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];

      if (!touch) {
        return;
      }

      setPoint(touch.clientX, touch.clientY);
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const touch = event.touches[0];

      if (touch) {
        setPoint(touch.clientX, touch.clientY);
        return;
      }

      clearTouchInput();
    };

    const handleScroll = () => {
      const nextScrollY = window.scrollY;
      const rawDelta = nextScrollY - motion.lastScrollY;
      const delta = Math.abs(rawDelta);
      const now = performance.now();
      const elapsed = Math.max(16, now - motion.lastScrollAt);
      const velocity = clamp(rawDelta / elapsed, -4.5, 4.5);

      motion.lastScrollY = nextScrollY;
      motion.lastScrollAt = now;

      const titleBounds = title.getBoundingClientRect();
      const titleVisible = titleBounds.bottom > -80 && titleBounds.top < window.innerHeight + 80;

      if (delta > 0 && titleVisible) {
        motion.scrollDirection = rawDelta >= 0 ? 1 : -1;
        const impulse = clamp(rawDelta * 0.2 + velocity * 12, -22, 22);

        motion.scrollCarry = clamp(motion.scrollCarry + impulse * 0.38, -28, 28);
        motion.scrollVelocity = clamp(motion.scrollVelocity + impulse * 0.28, -18, 18);
        motion.scrollEnergy = clamp(motion.scrollEnergy + Math.min(delta / 76, 0.68), 0, 1.5);
        motion.scrollReleaseAt = now + 260;
        start();
      }
    };

    const handleResize = () => {
      measureLetters();
      start();
    };

    const handleBlur = () => {
      motion.active = false;
      motion.dragging = false;
      motion.insideHero = false;
      motion.proximity = 0;
      start();
    };

    hero.addEventListener("pointerdown", handlePointerDown);
    measureLetters();
    document.fonts?.ready.then(() => {
      if (!disposed) {
        measureLetters();
        start();
      }
    });
    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("blur", handleBlur);

    return () => {
      disposed = true;
      hero.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("blur", handleBlur);

      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
      }

      resetLetters();
    };
  }, []);

  return (
    <h1 ref={titleRef} className="hero-title" aria-label={TITLE_WORDS.join(" ")}>
      {words.map((word) => (
        <span className="hero-title__word" aria-hidden="true" key={word.text}>
          {word.letters.map(({ char, index }) => (
            <span
              className="hero-title__letter"
              key={`${char}-${index}`}
              ref={(element) => {
                if (element) {
                  letterRefs.current[index] = element;
                }
              }}
            >
              {char}
            </span>
          ))}
        </span>
      ))}
    </h1>
  );
}
